// Icon library — verbatim port of the design's components.jsx `Icon`.
// Every SVG path is copied as-is; the union type below is the contract
// other dashboard components rely on for `name` autocomplete.

import type { JSX } from "react";

export type IconName =
  | "search"
  | "chevron-down"
  | "chevron-right"
  | "plus"
  | "x"
  | "send"
  | "chat"
  | "panel"
  | "user"
  | "users"
  | "git-branch"
  | "git-fork"
  | "issue"
  | "shield"
  | "shield-x"
  | "check"
  | "link"
  | "wand"
  | "scissors"
  | "compass"
  | "spark"
  | "code"
  | "terminal"
  | "transcript"
  | "filter"
  | "more"
  | "gate"
  | "history"
  | "sliders"
  | "open"
  | "in-progress"
  | "review"
  | "done"
  | "ready"
  | "pencil"
  | "inbox"
  | "flag"
  | "arrow-up"
  | "chevron-up"
  | "plus-circle"
  | "backlog";

interface IconProps {
  readonly name: IconName;
  readonly size?: number;
  readonly stroke?: number;
}

export const Icon = ({
  name,
  size = 14,
  stroke = 1.6,
}: IconProps): JSX.Element | null => {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "search":
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg {...props}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case "chevron-right":
      return (
        <svg {...props}>
          <path d="m9 6 6 6-6 6" />
        </svg>
      );
    case "plus":
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "x":
      return (
        <svg {...props}>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case "send":
      return (
        <svg {...props}>
          <path d="m22 2-7 20-4-9-9-4 20-7Z" />
        </svg>
      );
    case "chat":
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "panel":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M15 3v18" />
        </svg>
      );
    case "user":
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      );
    case "users":
      return (
        <svg {...props}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 21a7 7 0 0 1 14 0" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M22 19a5 5 0 0 0-5-5" />
        </svg>
      );
    case "git-branch":
      return (
        <svg {...props}>
          <circle cx="6" cy="3" r="2" />
          <circle cx="6" cy="21" r="2" />
          <circle cx="18" cy="7" r="2" />
          <path d="M6 5v14M18 9a4 4 0 0 1-4 4H6" />
        </svg>
      );
    case "git-fork":
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="12" cy="18" r="2" />
          <path d="M6 8v2a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4V8M12 14v2" />
        </svg>
      );
    case "issue":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="2.5" />
        </svg>
      );
    case "shield":
      return (
        <svg {...props}>
          <path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5z" />
        </svg>
      );
    case "shield-x":
      return (
        <svg {...props}>
          <path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5z" />
          <path d="m14 9-4 4M10 9l4 4" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="m5 12 5 5L20 7" />
        </svg>
      );
    case "link":
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
      );
    case "wand":
      return (
        <svg {...props}>
          <path d="m15 4 5 5L9 20l-5-5z" />
          <path d="m14 7 3 3" />
          <path d="M5 3v4M19 17v4M3 5h4M17 19h4" />
        </svg>
      );
    case "scissors":
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="m20 4-9 9M14 14l6 6M8.12 8.12 12 12" />
        </svg>
      );
    case "compass":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="m16 8-3 8-5 1 3-8z" />
        </svg>
      );
    case "spark":
      return (
        <svg {...props}>
          <path d="M12 3v4M12 17v4M5 12H1M23 12h-4M6 6l3 3M18 18l-3-3M6 18l3-3M18 6l-3 3" />
        </svg>
      );
    case "code":
      return (
        <svg {...props}>
          <path d="m8 8-5 4 5 4M16 8l5 4-5 4M14 4l-4 16" />
        </svg>
      );
    case "terminal":
      return (
        <svg {...props}>
          <path d="m4 8 4 4-4 4M12 16h8" />
        </svg>
      );
    case "transcript":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M7 8h10M7 12h10M7 16h6" />
        </svg>
      );
    case "filter":
      return (
        <svg {...props}>
          <path d="M3 5h18l-7 9v6l-4-2v-4z" />
        </svg>
      );
    case "more":
      return (
        <svg {...props}>
          <circle cx="6" cy="12" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="18" cy="12" r="1" />
        </svg>
      );
    case "gate":
      return (
        <svg {...props}>
          <path d="M3 21V9l9-6 9 6v12" />
          <path d="M9 21v-7h6v7" />
        </svg>
      );
    case "history":
      return (
        <svg {...props}>
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 8v4l3 2" />
        </svg>
      );
    case "sliders":
      return (
        <svg {...props}>
          <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h10M18 18h2" />
          <circle cx="16" cy="6" r="2" />
          <circle cx="8" cy="12" r="2" />
          <circle cx="16" cy="18" r="2" />
        </svg>
      );
    case "open":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
    case "in-progress":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 4a8 8 0 0 1 0 16" fill="currentColor" />
        </svg>
      );
    case "review":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 4v8l5 3" />
        </svg>
      );
    case "done":
      return (
        <svg {...props} fill="currentColor">
          <circle cx="12" cy="12" r="8" stroke="none" />
          <path d="m8 12 3 3 5-6" stroke="var(--on-fill)" fill="none" />
        </svg>
      );
    case "ready":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" strokeDasharray="2 2" />
        </svg>
      );
    case "pencil":
      return (
        <svg {...props}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...props}>
          <path d="M3 12h5l2 3h4l2-3h5" />
          <path d="M5 5h14l2 7v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z" />
        </svg>
      );
    case "flag":
      return (
        <svg {...props}>
          <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
        </svg>
      );
    case "arrow-up":
      return (
        <svg {...props}>
          <path d="M12 19V5M6 11l6-6 6 6" />
        </svg>
      );
    case "chevron-up":
      return (
        <svg {...props}>
          <path d="m6 15 6-6 6 6" />
        </svg>
      );
    case "plus-circle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      );
    case "backlog":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" strokeDasharray="1 3" />
        </svg>
      );
    default:
      return null;
  }
};
